import { z } from "zod";
import { ObjectMeta } from "../../common";
import { DbCreateInputOmit, DbUpdate } from "../../helpers";
import { OmitStrict } from "../../utils";

export type CreateDesignElement = DbCreateInputOmit<DesignElement>;
export type UpdateDesignElement = OmitStrict<
  DbUpdate<DesignElement>,
  "brandPersistentId" | "childType" | "designSystemVersionId" | "shortPersistentId"
>;

//
// Token types
//

export const DesignTokenType = z.enum([
  "Color",
  "Border",
  "Gradient",
  "Shadow",
  "Dimension",
  "Duration",
  "Size",
  "Space",
  "Opacity",
  "FontSize",
  "LineHeight",
  "LetterSpacing",
  "ParagraphSpacing",
  "BorderWidth",
  "BorderRadius",
  "ZIndex",
  "Image",
  "String",
  "ProductCopy",
  "FontFamily",
  "FontWeight",
  "TextDecoration",
  "TextCase",
  "Visibility",
  "Typography",
  "Blur",
  "Font",
]);

export const tokenElementTypes: DesignTokenType[] = [...DesignTokenType.options.filter(v => v !== "Font")];

export type DesignTokenType = z.infer<typeof DesignTokenType>;

//
// Non-token types
//

export const DesignElementType = DesignTokenType.or(
  z.enum([
    "Component",
    "Theme",
    "Documentation",
    "DocumentationPage",
    "DesignSystemComponent",
    "ElementGroup",
    "FigmaNodeStructure",
    "FigmaNodeReference",
    "PageBlock",
  ])
);

export type DesignElementType = z.infer<typeof DesignElementType>;

export function isTokenType(type: DesignElementType): type is DesignTokenType {
  return DesignTokenType.safeParse(type).success;
}

const numberTokenTypes: Set<DesignTokenType> = new Set([
  "Dimension",
  "Duration",
  "Size",
  "Space",
  "Opacity",
  "FontSize",
  "LineHeight",
  "LetterSpacing",
  "ParagraphSpacing",
  "BorderWidth",
  "BorderRadius",
  "ZIndex",
] satisfies DesignTokenType[]);

const stringTokenTypes: Set<DesignTokenType> = new Set([
  "String",
  "ProductCopy",
  "FontFamily",
  "FontWeight",
] satisfies DesignTokenType[]);

const fallbackNumberValue = 14;

export function areTokenTypesCompatible(
  lhs: DesignTokenType,
  rhs: DesignTokenType,
  isNonCompatibleTypeChangesEnabled = false
): boolean {
  if (lhs === rhs) return true;
  if (numberTokenTypes.has(lhs) && numberTokenTypes.has(rhs)) return true;
  if (stringTokenTypes.has(lhs) && stringTokenTypes.has(rhs)) return true;

  if (isNonCompatibleTypeChangesEnabled && stringTokenTypes.has(lhs) && numberTokenTypes.has(rhs)) return true;

  return false;
}

export function castStringToDimensionValue(lhs: DesignTokenType, rhs: DesignTokenType, value?: unknown) {
  if (stringTokenTypes.has(lhs) && numberTokenTypes.has(rhs) && value) {
    const newValue = Number.parseFloat(value?.toString() ?? "");
    const measure = Number.isNaN(newValue) ? fallbackNumberValue : newValue;
    return { unit: "Pixels", measure };
  }

  return value;
}

//
// Element category
//

export const DesignElementCategory = z.enum([
  "Token",
  "Component",
  "DesignSystemComponent",
  "DocumentationPage",
  "Theme",
  "PageBlock",
]);

export type DesignElementCategory = z.infer<typeof DesignElementCategory>;

// Elements

export const DesignSystemElementExportProps = z.object({
  isAsset: z
    .boolean()
    .nullish()
    .transform(v => v ?? false),

  codeName: z.string().nullish(),
});

export type DesignSystemElementExportProps = z.infer<typeof DesignSystemElementExportProps>;

export const ShallowDesignElement = z.object({
  id: z.string(),
  persistentId: z.string(),
  designSystemVersionId: z.string(),
  type: DesignElementType,
  brandPersistentId: z.string().optional(),
  collectionPersistentId: z.string().optional(),
  parentPersistentId: z.string().optional(),
  shortPersistentId: z.string().optional(),
  childType: DesignElementType.optional(),
  sortOrder: z.number(),
  origin: z.record(z.any()).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const DesignElement = ShallowDesignElement.extend({
  meta: ObjectMeta,
  slug: z.string().optional(),
  userSlug: z.string().optional(),
  exportProperties: DesignSystemElementExportProps.optional(),
  data: z.record(z.any()),
  origin: z.record(z.any()).optional(),
});

export type ShallowDesignElement = z.infer<typeof ShallowDesignElement>;
export type DesignElement = z.infer<typeof DesignElement>;

export const HierarchicalElements = DesignTokenType.or(
  z.enum(["Component", "DesignSystemComponent", "DocumentationPage"])
);

export type HierarchicalElements = z.infer<typeof HierarchicalElements>;
